/**
 * Date helpers anchored to America/Yellowknife so "today" defaults in date
 * pickers, file-name stamps, and lifecycle bounds match the user's local
 * calendar day instead of UTC. Without this, evening users (18:00+ NWT) get
 * tomorrow's date because `new Date().toISOString()` is always UTC.
 *
 * en-CA locale produces YYYY-MM-DD natively — convenient since most callers
 * feed the result into <input type="date"> or string-interpolation paths.
 */

/** Today's date in America/Yellowknife as a YYYY-MM-DD string. */
export const todayInNwt = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Yellowknife' }).format(new Date());

/** A given Date as a YYYY-MM-DD string in America/Yellowknife. */
export const dateInNwt = (d: Date): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Yellowknife' }).format(d);

const NWT_TZ = 'America/Yellowknife';

/**
 * Offset (localWallTime − UTC), in ms, of America/Yellowknife at the given UTC
 * instant. Negative for NWT (behind UTC): −6h in MDT, −7h in MST.
 */
const nwtOffsetMs = (utcMs: number): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: NWT_TZ, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const p: Record<string, number> = {};
  for (const part of parts) if (part.type !== 'literal') p[part.type] = parseInt(part.value, 10);
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asIfUtc - utcMs;
};

/**
 * #699 — UTC epoch ms for a wall-clock time on a YYYY-MM-DD calendar date in
 * America/Yellowknife, DST-aware. Any time component on the input is ignored;
 * only the calendar date is used. The two-pass offset lookup resolves the rare
 * DST-transition edge where the naive guess lands in the wrong offset.
 */
const nwtWallClockToUtcMs = (
  dateStr: string, h: number, m: number, s: number, ms: number,
): number => {
  const [y, mo, d] = dateStr.slice(0, 10).split('-').map(Number);
  // Resolve the offset on a millisecond-free instant (tz offsets are whole
  // minutes and nwtOffsetMs has no sub-second resolution), then add ms back.
  const guess = Date.UTC(y, mo - 1, d, h, m, s, 0);
  const off1 = nwtOffsetMs(guess);
  let utc = guess - off1;
  const off2 = nwtOffsetMs(utc);
  if (off2 !== off1) utc = guess - off2;
  return utc + ms;
};

/** UTC epoch ms of 00:00:00.000 (NWT local) on the given YYYY-MM-DD. */
export const nwtStartOfDayUtcMs = (dateStr: string): number =>
  nwtWallClockToUtcMs(dateStr, 0, 0, 0, 0);

/** UTC epoch ms of 23:59:59.999 (NWT local) on the given YYYY-MM-DD. */
export const nwtEndOfDayUtcMs = (dateStr: string): number =>
  nwtWallClockToUtcMs(dateStr, 23, 59, 59, 999);

/**
 * #866 U6 — a `<input type="datetime-local">` value (`YYYY-MM-DDTHH:mm`, no
 * offset) read as NWT wall-clock time, returned as a UTC ISO instant. The
 * picker has no zone; reading it in the browser's zone drew the wrong window
 * for anyone whose machine is not set to NWT. Null for anything unparseable.
 */
export const nwtLocalDateTimeToIso = (value: string | null | undefined): string | null => {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value ?? '');
  if (!m) return null;
  const ms = nwtWallClockToUtcMs(m[1], Number(m[2]), Number(m[3]), Number(m[4] ?? 0), 0);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
};

/**
 * #798 — a timestamp as NWT local date and time, `YYYY-MM-DD HH:mm`, 24-hour.
 *
 * Accepts what the AFF surfaces actually carry: epoch milliseconds on the map
 * path (`/aff/ac` projects `EXTRACT(EPOCH FROM ...) * 1000`), ISO strings on
 * the grid path, and a Date where one has already been built. One formatter for
 * all three keeps every AFF surface reading the same.
 *
 * A missing or unparseable value returns null rather than a formatted string.
 * `new Date(null)` is 1970-01-01, which would render as a real observation, and
 * an unparseable one would render as "Invalid Date" — both are worse than an
 * empty field, because they look like data.
 */
export const dateTimeInNwt = (value: string | number | Date | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') return null;

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: NWT_TZ, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  }).formatToParts(d);

  const p: Record<string, string> = {};
  for (const part of parts) if (part.type !== 'literal') p[part.type] = part.value;

  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
};
